// TopBar con logo, ricerca globale, notifiche e profilo utente
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Search, Bell, LogOut, Settings, User, Sun, Moon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAuth } from '../../hooks/useAuth'
import { useNotifications } from '../../hooks/useNotifications'
import { Avatar } from '../ui/Avatar'
import { useRealtimeStore } from '../../stores/realtime.store'

interface TopBarProps {
  sidebarCollapsed: boolean
  onOpenSearch: () => void
  onOpenNotifications: () => void
}

export function TopBar({ sidebarCollapsed, onOpenSearch, onOpenNotifications }: TopBarProps) {
  const { user, logout } = useAuth()
  const { unreadCount } = useNotifications()
  const connectionStatus = useRealtimeStore((s) => s.connectionStatus)
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  )

  // Scorciatoia Cmd+K / Ctrl+K per ricerca
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        onOpenSearch()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onOpenSearch])

  const toggleTheme = () => {
    const html = document.documentElement
    if (isDark) {
      html.classList.remove('dark')
    } else {
      html.classList.add('dark')
    }
    setIsDark(!isDark)
    localStorage.setItem('fc-theme', isDark ? 'light' : 'dark')
  }

  const connectionDot = {
    connected: 'bg-[var(--fc-success)]',
    connecting: 'bg-[var(--fc-warning)] animate-pulse',
    disconnected: 'bg-[var(--fc-gray-400)]',
    error: 'bg-[var(--fc-danger)]',
  }[connectionStatus]

  return (
    <header
      className={cn(
        'fixed right-0 top-0 z-30 flex h-16 items-center border-b border-[var(--fc-border)] bg-[var(--fc-surface)] px-4 gap-4',
        sidebarCollapsed ? 'left-16' : 'left-60'
      )}
      style={{ transition: 'left 200ms ease' }}
    >
      {/* Ricerca globale */}
      <button
        onClick={onOpenSearch}
        className={cn(
          'flex flex-1 max-w-sm items-center gap-2 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface-raised)] px-3 py-2',
          'text-sm text-[var(--fc-foreground-muted)] transition-colors hover:border-[var(--fc-primary)] hover:text-[var(--fc-foreground)]'
        )}
        aria-label="Apri ricerca globale (Cmd+K)"
      >
        <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="hidden sm:inline">Cerca...</span>
        <kbd className="ml-auto hidden rounded bg-[var(--fc-gray-200)] px-1.5 py-0.5 text-xs text-[var(--fc-foreground-subtle)] sm:inline">
          ⌘K
        </kbd>
      </button>

      <div className="ml-auto flex items-center gap-2">
        {/* Indicatore connessione WebSocket */}
        <div
          className={cn('h-2 w-2 rounded-full', connectionDot)}
          title={`WebSocket: ${connectionStatus}`}
          aria-label={`Connessione: ${connectionStatus}`}
        />

        {/* Toggle tema */}
        <button
          onClick={toggleTheme}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)] transition-colors"
          aria-label={isDark ? 'Passa a tema chiaro' : 'Passa a tema scuro'}
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {/* Notifiche */}
        <button
          onClick={onOpenNotifications}
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)] transition-colors"
          aria-label={`Notifiche${unreadCount > 0 ? ` (${unreadCount} non lette)` : ''}`}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--fc-danger)] text-[10px] font-bold text-white"
              aria-hidden="true"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* Profilo utente */}
        {user && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                className="flex items-center gap-2 rounded-lg p-1 hover:bg-[var(--fc-surface-overlay)] transition-colors"
                aria-label="Menu profilo"
              >
                <Avatar name={user.name} size="sm" />
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-[var(--fc-foreground)] leading-tight">{user.name}</p>
                  <p className="text-xs text-[var(--fc-foreground-muted)] leading-tight">{user.role}</p>
                </div>
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="z-50 min-w-[180px] overflow-hidden rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] shadow-[var(--fc-shadow-md)] p-1"
                sideOffset={8}
                align="end"
              >
                <DropdownMenu.Item asChild>
                  <Link
                    to="/settings"
                    className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-[var(--fc-foreground)] hover:bg-[var(--fc-surface-overlay)] outline-none"
                  >
                    <User className="h-4 w-4" />
                    Profilo
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Item asChild>
                  <Link
                    to="/settings"
                    className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-[var(--fc-foreground)] hover:bg-[var(--fc-surface-overlay)] outline-none"
                  >
                    <Settings className="h-4 w-4" />
                    Impostazioni
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="my-1 h-px bg-[var(--fc-border)]" />
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-[var(--fc-danger)] hover:bg-[color-mix(in_srgb,var(--fc-danger)_10%,transparent)] outline-none"
                  onSelect={() => void logout()}
                >
                  <LogOut className="h-4 w-4" />
                  Esci
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>
    </header>
  )
}
